import { CloseButton } from '@chakra-ui/react';
import { Drawer, FullBox, Portal } from '@halo/ui-react';
import type { RefObject } from 'react';

export interface DrawerPanelProps {
  title?: React.ReactNode;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  showClose?: boolean;
  triggerContent?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  container?: RefObject<HTMLDivElement | null>;
}

export const DrawerPanel = ({
  title,
  triggerContent,
  showClose,
  children,
  isOpen,
  setIsOpen,
  footer,
  container,
}: DrawerPanelProps) => {
  const [open, setOpen] = useState(isOpen);

  const onOpenChange = ({ open }: { open: boolean }) => {
    setOpen(open);
    setIsOpen?.(open);
  };

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} placement="bottom" contained>
      <Drawer.Trigger asChild>{triggerContent}</Drawer.Trigger>
      <Portal container={container}>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content
            borderTopRadius="4xl"
            borderBottomRadius="0"
            px="3.5"
            bg="bg.subtle"
            maxH="90%"
            minH="0"
          >
            <FullBox.Root column minH="0">
              {title && (
                <FullBox.Aside>
                  <Drawer.Title
                    className="DrawerPanelTitle"
                    pt="4"
                    pb="3.5"
                    textAlign="center"
                    fontSize="md"
                    fontWeight="bold"
                  >
                    {title}
                  </Drawer.Title>
                </FullBox.Aside>
              )}
              <FullBox.Main className="DrawerPanelContent" pb="3.5">
                {children}
              </FullBox.Main>
              {footer && (
                <FullBox.Aside pt="4" pb="3.5">
                  {footer}
                </FullBox.Aside>
              )}
            </FullBox.Root>
            {showClose && (
              <Drawer.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Drawer.CloseTrigger>
            )}
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};
